import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import BackButton from '@ts-core/components/BackButton';
import PageContentLayout from '@ts-core/core/components/PageContentLayout';

import StudentActivityAnswer from '@ts-core/modules/activities/components/ActivityAnswer';
import ActivityResult from '@ts-core/modules/activities/components/ActivityResult';
import ActivityBaseMessage from '@ts-core/modules/activities/components/ActivityBaseMessage';

import Typography from '@ts-core/components/Typography';

import { useActivity, useActivityAnswerActions, useActivityAnswer } from '../../hooks';

import ActivityAnswerFeedbackForm from '../../components/ActivityAnswerFeedbackForm';

const ActivityAnswerPage = () => {
  const { courseId, activityId, answerId } = useParams<{ courseId: string; activityId: string; answerId: string }>();
  const { activity, loading: loadingActivity } = useActivity({ courseId, activityId });
  const { answer, loading: loadingAnswer } = useActivityAnswer({ courseId, activityId, answerId });
  const {
    onGradeActivityAnswer,
    gradeActivityAnswerLoading,
    gradeActivityAnswerError,
    onCreateFeedback,
    createFeedbackLoading,
    createFeedbackError,
    onDeleteFeedback,
    onCreateScore,
    createScoreLoading,
    createScoreError,
    onDeleteScore,
  } = useActivityAnswerActions({
    courseId,
    activityId,
  });

  const handleGradeActivityAnswer = useCallback(
    (file?: File, text?: string, score?: string) => {
      if (answer) {
        onGradeActivityAnswer(answer.id, answer.student.id, file, text, score);
      }
    },
    [onGradeActivityAnswer, answer]
  );

  const handleCreateFeedback = useCallback(
    (file?: File, text?: string) => {
      if (answer) {
        onCreateFeedback(answer.id, answer.student.id, file, text);
      }
    },
    [onCreateFeedback, answer]
  );
  const handleCreateScore = useCallback(
    (score: string) => {
      if (answer) {
        onCreateScore(answer.id, score);
      }
    },
    [onCreateScore, answer]
  );

  const messageBoxWidth = {
    xs: '85%',
    sm: '70%',
    md: '60%',
  };

  const feedbackFormMode = useMemo((): 'feedback' | 'score' | 'full' | undefined => {
    if (!answer || (answer?.feedback && answer?.score)) return undefined;
    if (answer?.score) return 'feedback';
    if (answer?.feedback) return 'score';

    return 'full';
  }, [answer]);

  return (
    <Box>
      <BackButton href={`/${courseId}/activities/${activityId}/details/answers`} title={answer?.student?.name} subtitle="Respuestas" />
      <PageContentLayout>
        <Typography mb={2} variant="h6">
          {activity?.name}
        </Typography>
        <Stack spacing={2}>
          <Box display="flex" justifyContent="flex-end">
            <ActivityBaseMessage
              mode="professor"
              sx={{ width: messageBoxWidth }}
              title={activity?.createdBy.name}
              text={activity?.statement}
              loading={loadingActivity}
            />
          </Box>
          <Box sx={{ width: messageBoxWidth }}>
            <StudentActivityAnswer title={answer?.student?.name} answer={answer} loading={loadingAnswer} />
          </Box>
          {!!answer?.gradedAt && (
            <Box display="flex" justifyContent="flex-end">
              <ActivityResult
                sx={{ width: messageBoxWidth }}
                answer={answer}
                loading={loadingAnswer}
                onDeleteScore={() => onDeleteScore(answer)}
                onDeleteFeedback={() => onDeleteFeedback(answer)}
              />
            </Box>
          )}

          {feedbackFormMode && (
            <ActivityAnswerFeedbackForm
              mode={feedbackFormMode}
              sx={{ mt: 2 }}
              onGradeAnswer={handleGradeActivityAnswer}
              gradeLoading={gradeActivityAnswerLoading}
              gradeError={gradeActivityAnswerError}
              onCreateFeedback={handleCreateFeedback}
              createFeedbackLoading={createFeedbackLoading}
              createFeedbackError={createFeedbackError}
              onCreateScore={handleCreateScore}
              createScoreLoading={createScoreLoading}
              createScoreError={createScoreError}
            />
          )}
        </Stack>
      </PageContentLayout>
    </Box>
  );
};

export default ActivityAnswerPage;
